import { useCallback, useEffect, useState } from "react";
import type { Customer } from "@shared/api";
import {
  CUSTOMER_ADDRESS_MAX_GRAPHEMES,
  CUSTOMER_EMAIL_MAX_CHARS,
  CUSTOMER_NAME_MAX_GRAPHEMES,
  CUSTOMER_PHONE_MAX_CHARS,
  STANDING_ORDER_CADENCE_MAX_GRAPHEMES,
  graphemeLength,
} from "@shared/text";
import { TextField, Toggle } from "@client/ui";
import { apiFetch, ApiRequestError } from "@client/auth";
import "./sell.css";

/**
 * Customer picker sheet (F-901–905): opened from Sell's CartPanel at checkout and, retroactively, from
 * History's transaction detail (imported from there — this file is the shared home since Sell is its
 * primary use). Built from Sell's own hand-rolled sell__sheet markup, not @client/ui's <Sheet> — Sell
 * never imports that component (ModifierSheet.tsx and TenderScreen.tsx both hand-roll the same markup),
 * so this stays visually consistent with the rest of the screen. No search/filter input: this screen
 * keeps Sell's no-free-text rule (U-004); the trailing "+ New customer" tile is a deliberate, occasional
 * exception for full-keyboard entry, same as the one Setup already gets.
 */

/** D-049: the picker asks for the directory of the account that OWNS this register (see /api/customers). */
function customersPath(registerId: string | null | undefined): string {
  return registerId ? `/api/customers?registerId=${encodeURIComponent(registerId)}` : "/api/customers";
}

function messageFor(e: unknown, fallback: string): string {
  if (e instanceof ApiRequestError && e.status === 402) return "Customers is a Pro feature.";
  return e instanceof Error ? e.message : fallback;
}

export function CustomerPicker({ onPick, onClose, registerId = null, title = "Choose customer", currentCustomerId = null }: {
  /** Called with the chosen customer's id (null clears any previous pick), then the sheet closes itself. */
  onPick: (customerId: string | null, customer: Customer | null) => void;
  onClose: () => void;
  /**
   * The register this pick is for. Passed through to the API so a cashier device reads (and creates in)
   * the register OWNER's directory rather than its own account's (D-049). Optional only for tests.
   */
  registerId?: string | null;
  title?: string;
  /** The customer linked now, marked "Linked" on their tile (History's change flow, D-075). */
  currentCustomerId?: string | null;
}) {
  const [customers, setCustomers] = useState<Customer[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const list = await apiFetch<Customer[]>(customersPath(registerId));
      setCustomers(list);
    } catch (e) {
      // Never dead-end mid-sale: still render the grid (just the always-available tiles) plus the message.
      setCustomers([]);
      setError(messageFor(e, "Could not load customers."));
    }
  }, [registerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const pick = (id: string | null, customer: Customer | null) => {
    onPick(id, customer);
    onClose();
  };

  if (creating) {
    return (
      <NewCustomerSheet
        registerId={registerId}
        onCancel={() => setCreating(false)}
        onCreated={(customer) => pick(customer.id, customer)}
      />
    );
  }

  return (
    <section className="sell__sheet" role="dialog" aria-modal="true" aria-label={title}>
      <header className="sell__sheet-head">
        <h2 className="sell__sheet-title">{title}</h2>
        <button type="button" className="sell__btn sell__btn--quiet sell__sheet-close" onClick={onClose}>
          Cancel
        </button>
      </header>

      <div className="sell__sheet-body">
        {error ? (
          <p className="sell__error" role="alert">
            {error}
          </p>
        ) : null}
        {customers === null ? (
          <p className="sell__note">Loading customers…</p>
        ) : (
          <div className="sell__grid" role="group" aria-label="Customers">
            <button type="button" className="sell__tile" aria-label="No customer" onClick={() => pick(null, null)}>
              <span className="sell__tile-label">No customer</span>
            </button>
            {customers.map((c) => (
              <button
                key={c.id}
                type="button"
                className="sell__tile"
                aria-label={
                  (c.phone ? `${c.firstName} ${c.lastName}, ${c.phone}` : `${c.firstName} ${c.lastName}`) +
                  (c.id === currentCustomerId ? ", linked now" : "")
                }
                aria-current={c.id === currentCustomerId ? "true" : undefined}
                onClick={() => pick(c.id, c)}
              >
                {c.id === currentCustomerId ? (
                  <span className="sell__tile-badge" aria-hidden="true">
                    Linked
                  </span>
                ) : null}
                <span className="sell__tile-label">
                  {c.firstName} {c.lastName}
                </span>
                {c.phone ? (
                  <span className="sell__tile-price" aria-hidden="true">
                    {c.phone}
                  </span>
                ) : null}
              </button>
            ))}
            <button
              type="button"
              className="sell__tile sell__tile--add"
              onClick={() => setCreating(true)}
            >
              <span className="sell__tile-label">+ New customer</span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ inline "+ New customer" form

interface NewCustomerDraft {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  address: string;
  hasStandingOrder: boolean;
  standingOrderCadence: string;
}

function blankDraft(): NewCustomerDraft {
  return {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    address: "",
    hasStandingOrder: false,
    standingOrderCadence: "",
  };
}

/**
 * The same required fields as Setup's customer form (first and last name required; phone, email, address
 * and standing order optional, D-064). A deliberate, occasional full-keyboard action — not part of the fast per-tap selling
 * loop U-004 protects — so TextField/Toggle (OS keyboard) are used here same as in Setup.
 */
function NewCustomerSheet({ registerId, onCancel, onCreated }: {
  registerId: string | null;
  onCancel: () => void;
  onCreated: (customer: Customer) => void;
}) {
  const [draft, setDraft] = useState<NewCustomerDraft>(blankDraft);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Same bounds as the server's CreateCustomerBody (S-009, D-049), so Save never trips a generic 400.
  // First and last name are required; phone is optional (D-064).
  const firstOk = draft.firstName.trim().length > 0 && graphemeLength(draft.firstName) <= CUSTOMER_NAME_MAX_GRAPHEMES;
  const lastOk = draft.lastName.trim().length > 0 && graphemeLength(draft.lastName) <= CUSTOMER_NAME_MAX_GRAPHEMES;
  const phoneOk = draft.phone.trim().length <= CUSTOMER_PHONE_MAX_CHARS;
  const emailOk = draft.email.trim().length <= CUSTOMER_EMAIL_MAX_CHARS;
  const addressOk = graphemeLength(draft.address) <= CUSTOMER_ADDRESS_MAX_GRAPHEMES;
  const cadenceOk = !draft.hasStandingOrder || graphemeLength(draft.standingOrderCadence) <= STANDING_ORDER_CADENCE_MAX_GRAPHEMES;
  const canSave = firstOk && lastOk && phoneOk && emailOk && addressOk && cadenceOk && !busy;

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    setError(null);
    try {
      const created = await apiFetch<Customer>(customersPath(registerId), {
        method: "POST",
        json: {
          firstName: draft.firstName.trim(),
          lastName: draft.lastName.trim(),
          phone: draft.phone.trim() || null,
          email: draft.email.trim() || null,
          address: draft.address.trim() || null,
          hasStandingOrder: draft.hasStandingOrder,
          standingOrderCadence: draft.hasStandingOrder ? draft.standingOrderCadence.trim() || null : null,
        },
      });
      onCreated(created);
    } catch (e) {
      setError(messageFor(e, "Could not save this customer."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="sell__sheet" role="dialog" aria-modal="true" aria-label="New customer">
      <header className="sell__sheet-head">
        <h2 className="sell__sheet-title">New customer</h2>
        <button type="button" className="sell__btn sell__btn--quiet sell__sheet-close" onClick={onCancel}>
          Cancel
        </button>
      </header>

      <div className="sell__sheet-body">
        {error ? (
          <p className="sell__error" role="alert">
            {error}
          </p>
        ) : null}
        <TextField
          label="First name"
          value={draft.firstName}
          onChange={(firstName) => setDraft((d) => ({ ...d, firstName }))}
          helper="Required"
          maxGraphemes={CUSTOMER_NAME_MAX_GRAPHEMES}
        />
        <TextField
          label="Last name"
          value={draft.lastName}
          onChange={(lastName) => setDraft((d) => ({ ...d, lastName }))}
          helper="Required"
          maxGraphemes={CUSTOMER_NAME_MAX_GRAPHEMES}
        />
        <TextField
          label="Phone"
          value={draft.phone}
          onChange={(phone) => setDraft((d) => ({ ...d, phone }))}
          helper="Optional"
          maxGraphemes={CUSTOMER_PHONE_MAX_CHARS}
        />
        <TextField
          label="Email"
          type="email"
          value={draft.email}
          onChange={(email) => setDraft((d) => ({ ...d, email }))}
          helper="Optional"
          maxGraphemes={CUSTOMER_EMAIL_MAX_CHARS}
        />
        <TextField
          label="Address"
          value={draft.address}
          onChange={(address) => setDraft((d) => ({ ...d, address }))}
          helper="Optional"
          maxGraphemes={CUSTOMER_ADDRESS_MAX_GRAPHEMES}
        />
        <Toggle
          label="Has a standing order"
          description="For a customer with a recurring pickup or shipment — flag them here and note how often."
          checked={draft.hasStandingOrder}
          onChange={(hasStandingOrder) => setDraft((d) => ({ ...d, hasStandingOrder }))}
        />
        {draft.hasStandingOrder ? (
          <TextField
            label="Cadence"
            value={draft.standingOrderCadence}
            onChange={(standingOrderCadence) => setDraft((d) => ({ ...d, standingOrderCadence }))}
            maxGraphemes={STANDING_ORDER_CADENCE_MAX_GRAPHEMES}
            placeholder="e.g. Every Tuesday, or 1st of the month"
          />
        ) : null}
      </div>

      <footer className="sell__sheet-foot">
        <button type="button" className="sell__btn sell__btn--block" disabled={!canSave} onClick={() => void save()}>
          {busy ? "Saving…" : "Save"}
        </button>
      </footer>
    </section>
  );
}
